import { createColumnHelper } from '@tanstack/react-table';
import { Table, useSnackbar, Prompt } from '@weareopenr/component-library';
import { useMemo, useRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styles from './PricingTab.module.scss';
import { IPriceBandWithVenues } from '../../types/priceBand.types';
import { IMenuItem } from '../../types/menu.types';
import { PriceInput } from '../PricingTablePriceInput/PricingTablePriceInput';
import { DELIMITER, generatePriceFormDefaults, submitPrices } from 'src/helpers/priceTable';
import { PRICING_FORM_ID, PRICING_TABLE_OPTIONS_CONTAINER_ID } from 'src/constants';
import { IPricingChannel } from 'src/helpers/channels/getPricingChannels';
import { useRules } from '../../hooks/useRules/useRules';
import { IPriceRule } from '../../types/price.types';
import { useItem } from 'src/hooks/useItem/useItem';
import useProductPrices from 'src/hooks/useProductPrices/useProductPrices';
import { usePricingCategories } from 'src/hooks/usePricingCategories/usePricingCategories';
import { IPricingCategory } from 'src/api/getPricingCategories/types';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import SkeletonContainer from '../SkeletonContainer';
import PriceSchemeSelector from '../PriceSchemeSelector';
import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { PricingSavePortal } from '../PricingSavePortal';
import { useChannelFilter } from 'src/hooks/useChannelFilter';
import { ChannelFilter } from '../ChannelFilter';
import { usePriceBandFilter } from 'src/hooks/usePriceBandFilter';
import { PriceBandFilter } from '../PriceBandFilter';
import { NoResultsFound } from '../NoResultsFound';

const columnHelper = createColumnHelper<PricingTableRow>();

export type PricingTableRow = {
  priceBandId: string | null;
  priceBandName: string;
};

export interface PricingTabContentProps {
  pricingChannels: IPricingChannel[];
  visibleChannels: IPricingChannel[];
  priceBands: IPriceBandWithVenues[];
  visiblePriceBands: IPriceBandWithVenues[];
  hasNoPriceBandsSelected: boolean;
  item: IMenuItem;
  priceRules: IPriceRule[];
  priceSchemeId: string;
  onTotalChangesUpdate: (totalChanges: number) => void;
  pricingCategoryId?: string | null;
  pricingCategories?: IPricingCategory[];
}

const Content = ({
  pricingChannels,
  visibleChannels,
  priceBands,
  visiblePriceBands,
  hasNoPriceBandsSelected,
  item,
  priceRules,
  priceSchemeId,
  onTotalChangesUpdate,
  pricingCategoryId,
  pricingCategories,
}: PricingTabContentProps) => {
  const { showSnackbar } = useSnackbar();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.item] });
  };

  const tableData: PricingTableRow[] = useMemo(
    () =>
      hasNoPriceBandsSelected
        ? []
        : [
            { priceBandId: null, priceBandName: 'Default' },
            ...visiblePriceBands
              .filter((band) => band.priceBandId !== null)
              .map(({ name, priceBandId }) => ({
                priceBandId: priceBandId,
                priceBandName: name,
              })),
          ],
    [visiblePriceBands, hasNoPriceBandsSelected],
  );

  const products = useMemo(() => {
    return [
      {
        item_id: item.itemId,
        prices: item.prices,
      },
    ];
  }, [item]);

  // Default values can only be set on initial render, so we can safely ignore the dependencies here
  const defaultValues = useMemo(() => generatePriceFormDefaults(products, priceBands, pricingChannels), []);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { dirtyFields },
  } = useForm<Record<string, string>>({
    defaultValues,
  });

  const totalChanges = Object.keys(dirtyFields).length;

  useEffect(() => {
    onTotalChangesUpdate(totalChanges);
  }, [totalChanges, onTotalChangesUpdate]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('priceBandName', {
        size: 150,
        header: 'Price Band',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('priceBandId', {
        header: 'Default Price',
        id: 'defaultPrice',
        size: 150,
        cell: (info) => {
          const inputName = `${item.itemId}${DELIMITER}null${DELIMITER}${info.getValue()}`;
          return (
            <PriceInput
              control={control}
              inputName={inputName}
              register={register}
              priceRules={priceRules}
              tableContainerRef={tableContainerRef}
              categoryId={pricingCategoryId}
              pricingCategories={pricingCategories}
            />
          );
        },
      }),
      ...visibleChannels.map((channel, index) => {
        return columnHelper.accessor('priceBandId', {
          id: channel?.extensionInstanceId || `Channel-${index}`,
          header: () => <span title={channel.extensionInstanceName}>{channel.extensionInstanceName}</span>,
          size: 150,
          cell: (info) => {
            const inputName = `${item.itemId}${DELIMITER}${channel.extensionInstanceId}${DELIMITER}${info.getValue()}`;
            return (
              <PriceInput
                control={control}
                inputName={inputName}
                register={register}
                priceRules={priceRules}
                tableContainerRef={tableContainerRef}
                categoryId={pricingCategoryId}
                pricingCategories={pricingCategories}
              />
            );
          },
        });
      }),
    ],
    [visibleChannels, item, priceRules, pricingCategoryId, pricingCategories],
  );

  const onSubmit = submitPrices(dirtyFields, products, reset, showSnackbar, invalidate, priceSchemeId);

  return (
    <>
      <PricingSavePortal control={control} reset={reset} portalTargetId={PRICING_TABLE_OPTIONS_CONTAINER_ID} />
      <form id={PRICING_FORM_ID} className={styles['form-container']} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles['table-container']}>
          <Table
            className={styles['table-fill-height']}
            data={tableData}
            columns={columns}
            columnsToPin={['priceBandName', 'defaultPrice']}
            tableContainerRef={tableContainerRef}
            EmptyState={() => (
              <div className={styles['no-results']}>
                <NoResultsFound />
              </div>
            )}
          />
        </div>
      </form>
    </>
  );
};

const PricingDataLoader = ({
  itemId,
  priceSchemeId,
  onTotalChangesUpdate,
}: {
  itemId: string;
  priceSchemeId: string;
  onTotalChangesUpdate: (totalChanges: number) => void;
}) => {
  const { data: menuItem, isLoading: isItemLoading } = useItem(itemId, priceSchemeId);
  const { data: priceRules, isLoading: isPriceRulesLoading } = useRules(priceSchemeId);
  const { pricingChannels, visibleChannels, isLoading: isExternalSystemsLoading } = useChannelFilter();
  const {
    priceBands,
    visiblePriceBands,
    hasNoPriceBandsSelected,
    isLoading: isPriceBandsLoading,
    isFetching: isPriceBandsFetching,
  } = usePriceBandFilter(priceSchemeId);
  const { orderedFlatCategories } = usePricingCategories(priceSchemeId);
  const { data: productPricesData, isLoading: isProductPricesLoading } = useProductPrices(priceSchemeId, 50, {
    searchParam: menuItem?.name,
    enabled: !!menuItem?.name,
  });

  const pricingCategoryId = useMemo(() => {
    const products = productPricesData?.pages.flatMap((page) => page.data) ?? [];
    return products.find((p) => p.item_id === itemId)?.pricing_category?.id ?? null;
  }, [productPricesData, itemId]);

  const isLoadingPricingData =
    isExternalSystemsLoading ||
    isPriceBandsLoading ||
    isPriceBandsFetching ||
    isPriceRulesLoading ||
    isItemLoading ||
    isProductPricesLoading;

  if (isLoadingPricingData) {
    return <SkeletonContainer hiddenTitle={true} />;
  }

  return (
    <Content
      pricingChannels={pricingChannels}
      visibleChannels={visibleChannels}
      priceBands={priceBands}
      visiblePriceBands={visiblePriceBands}
      hasNoPriceBandsSelected={hasNoPriceBandsSelected}
      item={menuItem}
      priceRules={priceRules}
      priceSchemeId={priceSchemeId}
      onTotalChangesUpdate={onTotalChangesUpdate}
      pricingCategoryId={pricingCategoryId}
      pricingCategories={orderedFlatCategories}
    />
  );
};

const PricingTabPriceBandFilter = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const { priceBandOptions, selectedPriceBands, setSelectedPriceBands } = usePriceBandFilter(priceSchemeId);
  return <PriceBandFilter options={priceBandOptions} selected={selectedPriceBands} onChange={setSelectedPriceBands} />;
};

export const PricingTab = ({ itemId }: { itemId: string }) => {
  const { selectedPriceSchemeId, setSelectedPriceSchemeId } = useSelectedPriceScheme();
  const [totalChanges, setTotalChanges] = useState(0);
  const [nextPriceSchemeId, setNextPriceSchemeId] = useState<string | null>(null);
  const { channelOptions, searchChannels, setSearchChannels } = useChannelFilter();

  const handlePriceSchemeChange = (newSchemeId: string) => {
    if (totalChanges > 0) {
      setNextPriceSchemeId(newSchemeId);
    } else {
      setSelectedPriceSchemeId(newSchemeId);
    }
  };

  const handleConfirmSchemeChange = () => {
    if (nextPriceSchemeId) {
      setSelectedPriceSchemeId(nextPriceSchemeId);
      setNextPriceSchemeId(null);
    }
  };

  const handleCancelSchemeChange = () => {
    setNextPriceSchemeId(null);
  };

  if (!selectedPriceSchemeId) {
    return <SkeletonContainer hiddenTitle={true} />;
  }

  return (
    <div className={styles['container']}>
      <Prompt
        closePrompt={handleCancelSchemeChange}
        open={nextPriceSchemeId !== null}
        buttonHandler={handleConfirmSchemeChange}
        buttonText="Leave page"
        backButton
        backButtonText="Review changes"
        promptHeading="Unsaved changes"
        size="small"
      >
        {`You have ${totalChanges} unsaved ${totalChanges === 1 ? 'change' : 'changes'}. Please review and action.`}
      </Prompt>
      <div className={styles['controls-row']}>
        <div className={styles['filters']}>
          <PriceSchemeSelector value={selectedPriceSchemeId} onChange={handlePriceSchemeChange} />
          <ChannelFilter options={channelOptions} selected={searchChannels} onChange={setSearchChannels} />
          <PricingTabPriceBandFilter priceSchemeId={selectedPriceSchemeId} />
        </div>
        <div id={PRICING_TABLE_OPTIONS_CONTAINER_ID} />
      </div>
      <PricingDataLoader itemId={itemId} priceSchemeId={selectedPriceSchemeId} onTotalChangesUpdate={setTotalChanges} />
    </div>
  );
};

export default PricingTab;
